import React, { useState, useId } from "react";

/* Smooth, gradient-filled revenue chart (pure SVG, no libraries).
   Same props/data as before: current[], previous[], meta{}.
   Each point: { label, fullLabel, revenue (null = future), orders, partial } */

const CUR_COLOR = "#3b7dd8";
const PREV_COLOR = "#f0b429";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const shortInr = (v) => {
  if (v >= 10000000) return `₹${+(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `₹${+(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${+(v / 1000).toFixed(1)}k`;
  return `₹${Math.round(v)}`;
};

// Tidy y-axis step so four steps cover the biggest value
const niceStep = (max) => {
  const raw = (max || 1000) / 4;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / pow;
  const mult = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return mult * pow;
};

const orderText = (n) => `${n} order${n === 1 ? "" : "s"}`;

/* Monotone cubic interpolation (Fritsch–Carlson): smooth curve that never
   overshoots the data points (so it can't dip below zero). */
const smoothPath = (pts) => {
  const n = pts.length;
  if (n === 0) return "";
  if (n === 1) return `M${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;

  const dx = [], m = [], t = new Array(n);
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1].x - pts[i].x;
    m[i] = (pts[i + 1].y - pts[i].y) / dx[i];
  }
  t[0] = m[0];
  t[n - 1] = m[n - 2];
  for (let i = 1; i < n - 1; i++) {
    t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i] / m[i], b = t[i + 1] / m[i];
    const s = a * a + b * b;
    if (s > 9) {
      const k = 3 / Math.sqrt(s);
      t[i] = k * a * m[i];
      t[i + 1] = k * b * m[i];
    }
  }

  let d = `M${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += ` C${(pts[i].x + h).toFixed(1)} ${(pts[i].y + t[i] * h).toFixed(1)}, ` +
         `${(pts[i + 1].x - h).toFixed(1)} ${(pts[i + 1].y - t[i + 1] * h).toFixed(1)}, ` +
         `${pts[i + 1].x.toFixed(1)} ${pts[i + 1].y.toFixed(1)}`;
  }
  return d;
};

const RevenueLineChart = ({ current = [], previous = [], meta = {} }) => {
  const uid = useId().replace(/:/g, "");
  const [hover, setHover] = useState(null);
  const n = Math.max(current.length, previous.length);

  if (!n) {
    return (
      <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "32px 0", fontSize: "0.88rem" }}>
        No revenue data yet.
      </div>
    );
  }

  const W = 520, H = 240;
  const P = { top: 22, right: 12, bottom: 28, left: 50 };
  const cw = W - P.left - P.right;
  const ch = H - P.top - P.bottom;
  const baseY = P.top + ch;

  const values = [...current, ...previous].map((d) => d.revenue).filter((v) => v != null);
  const step = niceStep(Math.max(...values, 0));
  const top = step * 4;

  const x = (i) => P.left + (cw * (i + 0.5)) / n;
  const y = (v) => P.top + ch - (v / top) * ch;

  // Split into continuous segments (a null revenue = future period, breaks the line)
  const segments = (arr) => {
    const segs = [];
    let cur = [];
    arr.forEach((pt, i) => {
      if (pt.revenue == null) { if (cur.length) segs.push(cur); cur = []; return; }
      cur.push({ x: x(i), y: y(pt.revenue) });
    });
    if (cur.length) segs.push(cur);
    return segs;
  };

  const buildSeries = (arr) =>
    segments(arr).map((seg) => {
      const line = smoothPath(seg);
      const area = seg.length > 1
        ? `${line} L${seg[seg.length - 1].x.toFixed(1)} ${baseY} L${seg[0].x.toFixed(1)} ${baseY} Z`
        : "";
      return { line, area };
    });

  const curSegs = buildSeries(current);
  const prevSegs = buildSeries(previous);

  const c = hover != null ? current[hover] : null;
  const p = hover != null ? previous[hover] : null;
  let change = null;
  if (c && p && c.revenue != null && p.revenue != null) {
    if (p.revenue > 0) change = ((c.revenue - p.revenue) / p.revenue) * 100;
    else if (c.revenue > 0) change = "new";
  }
  const shift = hover === 0 ? "-12%" : hover === n - 1 ? "-88%" : "-50%";

  // Tooltip sits just above the higher of the two hovered points
  let tipTopPct = 0;
  if (hover != null) {
    const ys = [c, p].filter((q) => q && q.revenue != null).map((q) => y(q.revenue));
    tipTopPct = ((ys.length ? Math.min(...ys) : P.top) / H) * 100;
  }

  const Legend = ({ color, label }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.76rem", color: "var(--text-muted)" }}>
      <span style={{ width: 18, height: 3, borderRadius: 2, background: color, display: "inline-block" }} />
      {label}
    </span>
  );

  const gCur = `rev-cur-${uid}`;
  const gPrev = `rev-prev-${uid}`;
  const gFade = `rev-fade-${uid}`;
  const mFade = `rev-mask-${uid}`;

  return (
    <div onMouseLeave={() => setHover(null)}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
        <Legend color={CUR_COLOR} label={meta.currentLabel || "Current"} />
        <Legend color={PREV_COLOR} label={meta.previousLabel || "Earlier"} />
      </div>

      <div style={{ position: "relative" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={`Revenue: ${meta.currentLabel || "current"} compared with ${meta.previousLabel || "earlier"}`}
          style={{ width: "100%", height: "auto", display: "block", touchAction: "manipulation", overflow: "visible" }}
        >
          <defs>
            <linearGradient id={gCur} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CUR_COLOR} stopOpacity="0.22" />
              <stop offset="100%" stopColor={CUR_COLOR} stopOpacity="0.04" />
            </linearGradient>
            <linearGradient id={gPrev} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={PREV_COLOR} stopOpacity="0.20" />
              <stop offset="100%" stopColor={PREV_COLOR} stopOpacity="0.03" />
            </linearGradient>
            {/* Whole chart area fades out towards the bottom */}
            <linearGradient id={gFade} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="1" />
              <stop offset="70%" stopColor="#fff" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <mask id={mFade}>
              <rect x="0" y="0" width={W} height={H} fill={`url(#${gFade})`} />
            </mask>
          </defs>

          {/* Soft vertical bands, one per period */}
          {Array.from({ length: n }, (_, i) => {
            const bw = Math.min(14, (cw / n) * 0.5);
            return (
              <rect
                key={i}
                x={x(i) - bw / 2} y={P.top} width={bw} height={ch}
                rx={bw / 2}
                fill={CUR_COLOR}
                opacity={hover === i ? 0.14 : 0.06}
                style={{ transition: "opacity .15s" }}
              />
            );
          })}

          {/* Dotted horizontal grid + y labels */}
          {[0, 1, 2, 3, 4].map((i) => {
            const v = step * i;
            return (
              <g key={i}>
                <line x1={P.left} x2={W - P.right} y1={y(v)} y2={y(v)} stroke="#d5dae1" strokeWidth={1} strokeDasharray="1.5 3" />
                <text x={P.left - 8} y={y(v) + 3} textAnchor="end" fontSize={9} fill="#9ca3af">{shortInr(v)}</text>
              </g>
            );
          })}

          {/* Areas + lines (earlier first, current on top), faded at the bottom */}
          <g mask={`url(#${mFade})`}>
            {prevSegs.map((s, k) => s.area && <path key={`pa${k}`} d={s.area} fill={`url(#${gPrev})`} />)}
            {curSegs.map((s, k) => s.area && <path key={`ca${k}`} d={s.area} fill={`url(#${gCur})`} />)}
          </g>
          {prevSegs.map((s, k) => (
            <path key={`pl${k}`} d={s.line} fill="none" stroke={PREV_COLOR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {curSegs.map((s, k) => (
            <path key={`cl${k}`} d={s.line} fill="none" stroke={CUR_COLOR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ))}

          {/* Hover markers: white-filled rings, only on the hovered period */}
          {hover != null && p && p.revenue != null && (
            <circle cx={x(hover)} cy={y(p.revenue)} r={4.5} fill="#fff" stroke={PREV_COLOR} strokeWidth={2} />
          )}
          {hover != null && c && c.revenue != null && (
            <circle cx={x(hover)} cy={y(c.revenue)} r={4.5} fill="#fff" stroke={CUR_COLOR} strokeWidth={2} />
          )}

          {/* X-axis labels */}
          {Array.from({ length: n }, (_, i) => (
            <text
              key={i}
              x={x(i)} y={H - P.bottom + 16}
              textAnchor="middle" fontSize={9}
              fill={hover === i ? "#111" : "#6b7280"}
              fontWeight={hover === i ? 700 : 400}
            >
              {(current[i] || previous[i]).label}
            </text>
          ))}

          {/* Invisible hover / tap columns */}
          {Array.from({ length: n }, (_, i) => (
            <rect
              key={i}
              x={x(i) - cw / (2 * n)} y={P.top} width={cw / n} height={ch}
              fill="transparent"
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHover(i)}
              onMouseMove={() => setHover(i)}
              onClick={() => setHover(i)}
              onTouchStart={() => setHover(i)}
            />
          ))}
        </svg>

        {/* Dark tooltip with arrow */}
        {hover != null && c && p && (
          <div
            style={{
              position: "absolute",
              top: `calc(${tipTopPct}% - 14px)`,
              left: `${(x(hover) / W) * 100}%`,
              transform: `translate(${shift}, -100%)`,
              background: "#1f2937",
              color: "#fff",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
              padding: "9px 12px",
              fontSize: "0.76rem",
              minWidth: 168,
              pointerEvents: "none",
              zIndex: 5,
            }}
          >
            {[{ pt: c, color: CUR_COLOR }, { pt: p, color: PREV_COLOR }].map(({ pt, color }, k) => (
              <div key={k} style={{ marginBottom: k === 0 ? 7 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
                  {pt.fullLabel}
                  {pt.partial && <span style={{ fontWeight: 500, color: "#9ca3af" }}> (in progress)</span>}
                </div>
                {pt.revenue == null ? (
                  <div style={{ color: "#9ca3af", paddingLeft: 14 }}>Not yet</div>
                ) : (
                  <div style={{ paddingLeft: 14, color: "#e5e7eb" }}>
                    {inr(pt.revenue)} <span style={{ color: "#9ca3af" }}>· {orderText(pt.orders)}</span>
                  </div>
                )}
              </div>
            ))}
            {change != null && (
              <div
                style={{
                  marginTop: 7, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.15)", fontWeight: 700,
                  color: change === "new" || change >= 0 ? "#34d399" : "#f87171",
                }}
              >
                {change === "new"
                  ? "New — no sales in the earlier period"
                  : `${change >= 0 ? "▲" : "▼"} ${Math.abs(change).toFixed(0)}% vs ${meta.previousLabel || "earlier"}`}
              </div>
            )}
            {/* arrow */}
            <span
              style={{
                position: "absolute", bottom: -5,
                left: `calc(50% + ${hover === 0 ? "calc(38% )" : hover === n - 1 ? "calc(-38%)" : "0px"})`,
                width: 10, height: 10, background: "#1f2937",
                transform: "translateX(-50%) rotate(45deg)",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueLineChart;
